import React from "react";
import { MagnifyingGlass, SortAscending, SortDescending, X } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

/**
 * SearchSortBar — drop-in bar pairing with `useSearchSort` hook.
 *
 *   const s = useSearchSort(items, { searchKeys: [...], sortOptions: [...] });
 *   <SearchSortBar controller={s} placeholder="Search contracts…" testIdPrefix="repo" />
 *   {s.items.map(...)}
 */
export default function SearchSortBar({ controller, placeholder = "Search…", testIdPrefix = "list", rightSlot }) {
  const { q, setQ, sortBy, setSortBy, sortDir, setSortDir, sortOptions, total, matched } = controller;
  const showCount = q && total !== matched;

  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-5 items-stretch sm:items-center" data-testid={`${testIdPrefix}-search-sort-bar`}>
      <div className="relative flex-1 min-w-[220px]">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
        <Input
          type="text"
          placeholder={placeholder}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-10 pl-9 pr-9"
          data-testid={`${testIdPrefix}-search-input`}
        />
        {q && (
          <button
            onClick={() => setQ("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--text-3)] hover:text-[var(--text)]"
            aria-label="Clear search"
            data-testid={`${testIdPrefix}-search-clear`}
          >
            <X size={12} />
          </button>
        )}
      </div>

      {showCount && (
        <div className="text-xs text-[var(--text-3)] whitespace-nowrap" data-testid={`${testIdPrefix}-search-count`}>
          {matched} of {total}
        </div>
      )}

      {sortOptions?.length > 0 && (
        <div className="flex items-center gap-2">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[170px] h-10" data-testid={`${testIdPrefix}-sort-by`}>
              <SelectValue placeholder="Sort by…" />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map(o => (
                <SelectItem key={o.value} value={o.value} data-testid={`${testIdPrefix}-sort-option-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
            aria-label={`Sort ${sortDir === "asc" ? "descending" : "ascending"}`}
            data-testid={`${testIdPrefix}-sort-dir`}
            className="h-10 w-10"
          >
            {sortDir === "asc" ? <SortAscending size={14} /> : <SortDescending size={14} />}
          </Button>
        </div>
      )}

      {rightSlot}
    </div>
  );
}
